import React, { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Coffee, MapPin, Clock, Users, CalendarDays, Navigation, Phone } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

import { LANGS, T } from "../lib/breakfastI18n";

// Public breakfast voucher: shown by the guest at the partner bar. Elegant, printable, no login. IT/EN switch.
export default function BreakfastVoucherPage() {
  const { code } = useParams();
  const [params, setParams] = useSearchParams();
  const [v, setV] = useState(null);
  const [err, setErr] = useState(false);
  const [lang, setLang] = useState(LANGS.includes(params.get("lang")) ? params.get("lang") : null);
  useEffect(() => { axios.get(`${API}/breakfast/public/${code}`).then((r) => { setV(r.data); setLang((l) => l || (LANGS.includes(r.data.lang) ? r.data.lang : "it")); }).catch(() => setErr(true)); }, [code]);

  const L = lang || "it";
  const t = T[L];
  const switchLang = (l) => { setLang(l); setParams(l === "it" ? {} : { lang: l }, { replace: true }); };

  if (err) return <div className="min-h-screen bg-[#2b1410] text-[#f7ebdf] flex items-center justify-center p-6 text-center" data-testid="breakfast-voucher-error"><div><Coffee className="h-10 w-10 mx-auto mb-3 text-[#C48B31]" /><p className="font-serif text-2xl">{t.notfound}</p></div></div>;
  if (!v) return <div className="min-h-screen bg-[#2b1410]" />;
  const cancelled = v.status === "cancelled";
  const bar = v.bar;
  const dp = v.date_parts?.[L] || { weekday: v.date_label.split(" ")[0], day_month: v.date_label.split(" ").slice(1, 3).join(" ") };

  return (
    <div className="min-h-screen bg-[#2b1410] text-[#2b1410] flex items-start justify-center sm:py-8" data-testid="breakfast-voucher-page" data-lang={L}>
      <div className="w-full max-w-md bg-[#f7ebdf] sm:rounded-[2rem] overflow-hidden shadow-2xl relative">
        <div className="relative h-56">
          <img src={bar.hero_url || "/media/breakfast-bar-hero.jpg"} alt="" className="absolute inset-0 h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#2b1410] via-[#2b1410]/30 to-transparent" />
          <div className="absolute top-4 left-4 right-4 text-[#f7ebdf]">
            <div className="flex items-center justify-between">
              <span className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] font-semibold"><Coffee className="h-4 w-4 text-[#C48B31]" /> {t.kicker}</span>
              <span className="font-mono text-sm tracking-widest bg-black/30 backdrop-blur px-2.5 py-1 rounded-full" data-testid="breakfast-voucher-code-small">{v.code}</span>
            </div>
            <div className="mt-2 flex justify-end">
              <div className="flex rounded-full bg-black/30 backdrop-blur p-0.5 text-[11px] font-bold" data-testid="breakfast-lang-switch">
                {LANGS.map((l) => <button key={l} onClick={() => switchLang(l)} className={`px-2 py-0.5 rounded-full uppercase ${L === l ? "bg-[#f7ebdf] text-[#2b1410]" : "text-[#f7ebdf]/80"}`} data-testid={`breakfast-lang-${l}`}>{l}</button>)}
              </div>
            </div>
          </div>
          <div className="absolute bottom-4 left-5 right-5 text-[#f7ebdf]">
            <div className="text-[11px] uppercase tracking-[0.25em] text-[#C48B31] font-semibold">{t.from}</div>
            <h1 className="font-serif text-2xl sm:text-3xl font-bold leading-tight">{t.title}</h1>
          </div>
        </div>

        <div className="px-5 -mt-1 pb-6">
          <div className="rounded-3xl bg-white shadow-lg border border-[#e8dcc8] p-5 text-center" data-testid="breakfast-bar-card">
            <h2 className="font-serif text-3xl font-bold text-[#4a1c17] leading-tight" data-testid="breakfast-bar-name">{bar.bar_name}</h2>
            {bar.owner && <div className="text-sm text-[#7c5a4e] italic">{bar.owner}</div>}
            <div className="mt-3 text-sm text-[#2b1410] inline-flex items-start gap-2 text-left"><MapPin className="h-4 w-4 text-[#C48B31] mt-0.5 shrink-0" /> <span>{bar.address}</span></div>
            <a href={bar.maps_url} target="_blank" rel="noreferrer" className="mt-3 w-full h-12 rounded-2xl bg-[#4a1c17] text-[#f7ebdf] font-bold inline-flex items-center justify-center gap-2" data-testid="breakfast-maps-btn"><Navigation className="h-4 w-4" /> {t.maps}</a>
          </div>

          <div className={`mt-4 rounded-3xl border-2 border-dashed p-5 text-center ${cancelled ? "border-rose-300 bg-rose-50" : "border-[#C48B31] bg-[#fdf6e9]"}`} data-testid="breakfast-voucher-body">
            {cancelled && <div className="mb-2 inline-block rounded-full bg-rose-600 text-white px-3 py-1 text-xs font-bold uppercase tracking-wide" data-testid="breakfast-voucher-cancelled">{t.cancelled}</div>}
            <div className="text-[11px] uppercase tracking-[0.25em] text-[#7c5a4e] font-semibold">{t.code}</div>
            <div className="font-mono text-4xl font-bold tracking-[0.2em] text-[#4a1c17] my-1" data-testid="breakfast-voucher-code">{v.code}</div>
            <div className="text-lg font-serif font-bold text-[#2b1410]" data-testid="breakfast-voucher-guest">{v.guest}</div>
            <div className="mt-3 grid grid-cols-3 gap-2 text-xs">
              <Info icon={Users} label={t.people} value={v.people} testId="breakfast-voucher-people" />
              <Info icon={CalendarDays} label={t.valid} value={dp.day_month} sub={dp.weekday} testId="breakfast-voucher-date" />
              <Info icon={Clock} label={t.hours} value={<span className="text-xs whitespace-nowrap">{bar.hours}</span>} />
            </div>
            <div className="mt-4 text-sm text-[#2b1410]" data-testid="breakfast-voucher-includes"><span className="font-semibold">{t.each}</span> {bar.includes_i18n?.[L] || bar.includes}</div>
            <img src={`${API}/breakfast/public/${v.code}/qr.png`} alt="QR" className="mx-auto mt-4 h-28 w-28 rounded-xl bg-white p-1.5 border border-[#e8dcc8]" data-testid="breakfast-voucher-qr" />
            <div className="mt-2 text-[11px] text-[#7c5a4e]">{t.show}</div>
          </div>

          <div className="mt-5 text-center text-xs text-[#7c5a4e]">
            <div className="font-serif text-base font-bold text-[#4a1c17]">Agriturismo Il Pozzo dei Giganti</div>
            <div className="mt-1 inline-flex items-center gap-1"><Phone className="h-3 w-3" /> <a href={`https://wa.me/${v.hotel_phone}`} className="underline">{t.wa}</a></div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Info({ icon: Icon, label, value, sub, testId }) {
  return (
    <div className="rounded-2xl bg-white border border-[#e8dcc8] px-2 py-2" data-testid={testId}>
      <Icon className="h-3.5 w-3.5 mx-auto text-[#C48B31]" />
      <div className="text-[10px] uppercase tracking-wide text-[#7c5a4e] mt-0.5">{label}</div>
      <div className="font-bold text-[#2b1410] text-sm leading-tight capitalize">{value}</div>
      {sub && <div className="text-[10px] text-[#7c5a4e] capitalize">{sub}</div>}
    </div>
  );
}
